import { useEffect, useState } from "react";
import ModalCancelarTareaVista from "../vista/modalCancelarTareaVista";
import { type IModalCancelarTareaCVProps } from "../interfaces";
import { useNotificaciones } from "../../../globales/contextos/notificationContext";
import { EnumTipoNotificacion } from "../../../globales/interfaces";
import { ProveedorCargando, useCargando } from "../../../globales/contextos/cargandoContext";
import type { ICausalCancelacion } from "../../../modelos/CausalCancelacion/entidades/ICausalCancelacion";
import ModeloCausalCancelacion from "../../../modelos/CausalCancelacion";
import type { ITareaCancelar } from "../../../modelos/Tarea/entidades/ITareaCancelar";
import { TareaModelo } from "../../../modelos/Tarea";

const ModalCancelarTareaCV: React.FunctionComponent<IModalCancelarTareaCVProps> = ({
  abrirModalCancelarTarea,
  estadoModal,
  tareaSeleccionado
}) => {

  const modeloCausalCancelacion = new ModeloCausalCancelacion();
  const modeloTarea = new TareaModelo();
  const { mostrarNotificacion } = useNotificaciones();
  const { manejarCargando } = useCargando();
  const [datosCancelacion, setDatosCancelacion] = useState<ITareaCancelar>({
    TareaId: 0,
    CausalCancelacionId: 0,
    ComentariosCancelacion: ''
  });

  const [errores, setErrores] = useState<{
    causalCierre?: string;
    observacion?: string;
  }>({});
  const [causalesCancelacion, setCausalesCancelacion] = useState<ICausalCancelacion[]>([]);

  useEffect(() => {
    if (estadoModal) {
      setDatosCancelacion({
        TareaId: tareaSeleccionado?.Id || 0,
        CausalCancelacionId: 0,
        ComentariosCancelacion: ''
      })
      consultarCausalesCancelacion();

    } else {
      setDatosCancelacion({
        TareaId: 0,
        CausalCancelacionId: 0,
        ComentariosCancelacion: ''
      })
      setErrores({});
      setCausalesCancelacion([]);
    }
  }, [estadoModal])

  const consultarCausalesCancelacion = async () => {
    try {
      manejarCargando(true);
      const causales = await modeloCausalCancelacion.obtenerListaCausalesCancelacion();
      setCausalesCancelacion(causales.Data)
    } catch {
      mostrarNotificacion(
        EnumTipoNotificacion.Error,
        'Error al cargar datos de motivos',
        'No fue posible cargar los datos necesarios para los motivos. Por favor verifica tu conexión e intenta nuevamente.',
        5000
      );
    } finally {
      manejarCargando(false);
    }
  }

  const alCambiarValor = async (campo: keyof ITareaCancelar, valor: ITareaCancelar[keyof ITareaCancelar]) => {
    setDatosCancelacion((prevDatosCancelacion) => ({
      ...prevDatosCancelacion,
      [campo]: valor
    }));

    if (campo === 'CausalCancelacionId' && errores.causalCierre && valor) {
      setErrores(prev => ({ ...prev, causalCierre: undefined }));
    }

    if (campo === 'ComentariosCancelacion' && errores.observacion && valor.toString().trim()) {
      setErrores(prev => ({ ...prev, observacion: undefined }));
    }
  }


  const validarFormulario = (): boolean => {
    const nuevosErrores: typeof errores = {};

    if (!datosCancelacion.CausalCancelacionId) {
      nuevosErrores.causalCierre = 'Debe seleccionar un motivo de cierre';
    }

    if (!datosCancelacion.ComentariosCancelacion.trim()) {
      nuevosErrores.observacion = 'Debe ingresar una observación';
    } else if (datosCancelacion.ComentariosCancelacion.trim().length < 10) {
      nuevosErrores.observacion = 'La observación debe tener al menos 10 caracteres';
    }

    if (Object.keys(nuevosErrores).length > 0) {
      mostrarNotificacion(
        EnumTipoNotificacion.Error,
        'Error en el formulario',
        'Por favor corrige los errores antes de continuar.',
        4000
      );

    }
    setErrores(nuevosErrores);
    return Object.keys(nuevosErrores).length === 0;

  };

  const guardarCancelacion = async () => {
    if (!validarFormulario()) {
      return;
    }

    try {
      manejarCargando(true, "Cancelando tarea...", false);
      const respuesta = await modeloTarea.cancelarTarea(datosCancelacion);

      if (respuesta.Messages.length > 0) {
        respuesta.Messages.forEach(mensaje =>
          mostrarNotificacion(
            respuesta.NotificationType,
            '',
            mensaje,
            4000
          ));
      }

      if (respuesta.NotificationType === EnumTipoNotificacion.Success && respuesta.Data) {
        mostrarNotificacion(
          EnumTipoNotificacion.Success,
          'Tarea cancelada',
          `El tarea #${tareaSeleccionado.Id} ha sido cancelado exitosamente.`,
          3000
        );
        setDatosCancelacion({
          TareaId: 0,
          CausalCancelacionId: 0,
          ComentariosCancelacion: ''
        })
        abrirModalCancelarTarea(false, null);
      }
      setErrores({});

    } catch (error) {
      mostrarNotificacion(
        EnumTipoNotificacion.Error,
        'Error al cancelar tarea',
        'No fue posible cancelar el tarea. Por favor verifica tu conexión e intenta nuevamente.',
        5000
      );
    } finally {
      manejarCargando(false);
    }
  };


  return (
    <ProveedorCargando>
      <ModalCancelarTareaVista
        abrir={estadoModal}
        abrirModal={abrirModalCancelarTarea}
        tareaSeleccionado={tareaSeleccionado}
        datosCancelacion={datosCancelacion}
        alCambiarValor={alCambiarValor}
        guardarCancelacion={guardarCancelacion}
        errores={errores}
        causalesCancelacion={causalesCancelacion}
      />
    </ProveedorCargando>
  );
};

export default ModalCancelarTareaCV;
